import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDesignTheme, DP_THEMES } from "@/hooks/useDesignTheme";

/**
 * DesignPreviewShell — CP-inspired sidebar + top bar for /admin/design-preview/*
 *
 * READ-ONLY visual prototype. Zero effect on production /admin routes.
 * Provides:
 *  - Collapsible left sidebar with grouped nav (icons + labels)
 *  - Top strip with search, notifications, user menu
 *  - Mobile drawer (hamburger)
 *
 * Wrap any preview page in <DesignPreviewShell> ... </DesignPreviewShell>.
 */

const NAV = [
  {
    group: "Workspace",
    items: [
      { to: "/admin/design-preview", icon: "🏠", label: "Dashboard", exact: true },
      { to: "/admin/design-preview/inbox", icon: "📥", label: "Inbox", badge: 6 },
      { to: "/admin/design-preview/team-chat", icon: "💬", label: "Team Chat", badge: 3 },
      { to: "/admin/design-preview/live-feed", icon: "📡", label: "Live Feed" },
    ],
  },
  {
    group: "Operations",
    items: [
      { to: "/admin/design-preview/jobs", icon: "🧾", label: "Work Orders", badge: 128 },
      { to: "/admin/design-preview/schedule", icon: "📅", label: "Schedule" },
      { to: "/admin/design-preview/lineup", icon: "🚚", label: "Line-Up" },
      { to: "/admin/design-preview/sweeper", icon: "🧹", label: "Sweeper Radar", badge: 12 },
      { to: "/admin/design-preview/turns", icon: "🔄", label: "Turns Board" },
      { to: "/admin/design-preview/inspections", icon: "🔍", label: "Inspections" },
    ],
  },
  {
    group: "Portfolio",
    items: [
      { to: "/admin/design-preview/property", icon: "🏢", label: "Properties", badge: 247 },
      { to: "/admin/design-preview/landlords", icon: "👔", label: "Landlords" },
      { to: "/admin/design-preview/tenants", icon: "🧍", label: "Tenants" },
      { to: "/admin/design-preview/vendors", icon: "🔌", label: "Vendors" },
      { to: "/admin/design-preview/portfolios", icon: "🗂", label: "Portfolios" },
    ],
  },
  {
    group: "Billing",
    items: [
      { to: "/admin/design-preview/pmr-rebill", icon: "💰", label: "PMR Rebill" },
      { to: "/admin/design-preview/lcv-drop", icon: "📄", label: "LCV Invoices" },
      { to: "/admin/design-preview/ready-to-bill", icon: "✅", label: "Ready to Bill", badge: 8 },
      { to: "/admin/design-preview/bookkeeper", icon: "📚", label: "Bookkeeper" },
    ],
  },
  {
    group: "Radars",
    items: [
      { to: "/admin/design-preview/radar-move", icon: "🚛", label: "Move Radar" },
      { to: "/admin/design-preview/radar-legal", icon: "⚖️", label: "Legal Radar" },
      { to: "/admin/design-preview/radar-appliance", icon: "🧊", label: "Appliance IQ" },
      { to: "/admin/design-preview/radar-request", icon: "📢", label: "Request Radar" },
    ],
  },
  {
    group: "People",
    items: [
      { to: "/admin/design-preview/workers", icon: "👷", label: "Workers" },
      { to: "/admin/design-preview/subs", icon: "🤝", label: "Sub-Contractors" },
      { to: "/admin/design-preview/customers", icon: "🧑", label: "Customers" },
    ],
  },
  {
    group: "Integrations & AI",
    items: [
      { to: "/admin/design-preview/clickup", icon: "🔗", label: "ClickUp Sync" },
      { to: "/admin/design-preview/appfolio", icon: "🏦", label: "AppFolio" },
      { to: "/admin/design-preview/kraaft", icon: "📸", label: "Kraaft" },
      { to: "/admin/design-preview/twilio", icon: "📱", label: "Twilio SMS/MMS" },
      { to: "/admin/design-preview/integrations", icon: "🧩", label: "All Integrations" },
      { to: "/admin/design-preview/ai-tools", icon: "✨", label: "AI Tools" },
    ],
  },
  {
    group: "Admin",
    items: [
      { to: "/admin/design-preview/reports", icon: "📊", label: "Reports" },
      { to: "/admin/design-preview/activity", icon: "🕓", label: "Activity Log" },
      { to: "/admin/design-preview/cleanup", icon: "🧼", label: "Cleanup" },
      { to: "/admin/design-preview/settings", icon: "⚙️", label: "Settings" },
    ],
  },
];

export default function DesignPreviewShell({ children }) {
  const location = useLocation();
  const { theme, setTheme } = useDesignTheme();
  const [themeOpen, setThemeOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("dp-sidebar-collapsed") === "1"; } catch { return false; }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem("dp-groups") || "{}"); } catch { return {}; }
  });

  useEffect(() => {
    try { localStorage.setItem("dp-sidebar-collapsed", collapsed ? "1" : "0"); } catch {}
  }, [collapsed]);
  useEffect(() => {
    try { localStorage.setItem("dp-groups", JSON.stringify(collapsedGroups)); } catch {}
  }, [collapsedGroups]);
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const toggleGroup = (g) => setCollapsedGroups(p => ({ ...p, [g]: !p[g] }));

  const isActive = (to, exact) => {
    if (exact) return location.pathname === to;
    return location.pathname === to || location.pathname.startsWith(to + "/");
  };

  const sidebarWidth = collapsed ? "w-[64px]" : "w-[240px]";

  return (
    <div className="min-h-screen bg-[#0B1830] flex" data-testid="dp-shell">
      {/* ── MOBILE OVERLAY ─────────────────────────────────────── */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* ── SIDEBAR ────────────────────────────────────────────── */}
      <aside
        data-testid="dp-sidebar"
        className={`
          fixed lg:sticky top-0 left-0 h-screen z-50
          ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
          ${sidebarWidth}
          bg-[#0F1F3A] border-r border-white/5
          transition-all duration-200 flex flex-col
          overflow-y-auto overflow-x-hidden
        `}
        style={{ scrollbarWidth: "thin" }}
      >
        {/* Brand */}
        <div className="h-14 flex items-center justify-between px-3 border-b border-white/5 flex-shrink-0">
          <Link to="/admin/design-preview" className="flex items-center gap-2 min-w-0" data-testid="dp-brand">
            {collapsed ? (
              <div className="h-9 w-9 rounded-xl bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
                <img src="/brand/fr-mark.png" alt="Field Radar" className="h-6 w-6 object-contain"/>
              </div>
            ) : (
              <div className="h-9 px-2.5 rounded-xl bg-white flex items-center gap-2 shadow-sm">
                <img src="/brand/fr-lockup.png" alt="FieldRadar.pro" className="h-5 object-contain"/>
              </div>
            )}
            {!collapsed && (
              <span className="text-[9px] font-black uppercase tracking-widest text-[#00C4CC] leading-tight px-1.5 py-0.5 rounded-full bg-[#00C4CC]/10 border border-[#00C4CC]/30">
                Preview
              </span>
            )}
          </Link>
          <button
            onClick={() => setCollapsed(c => !c)}
            className="hidden lg:flex h-7 w-7 rounded-full hover:bg-white/10 text-white/60 items-center justify-center flex-shrink-0"
            data-testid="dp-sidebar-collapse"
            title={collapsed ? "Expand" : "Collapse"}
          >
            {collapsed ? "»" : "«"}
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-3 px-2 space-y-1">
          {NAV.map(group => {
            const gCollapsed = !!collapsedGroups[group.group];
            return (
              <div key={group.group} className="pb-1">
                {!collapsed && (
                  <button
                    onClick={() => toggleGroup(group.group)}
                    className="w-full flex items-center justify-between px-2 py-1.5 text-[9px] font-black uppercase tracking-widest text-white/40 hover:text-white/80"
                  >
                    <span>{group.group}</span>
                    <span className="text-[10px]">{gCollapsed ? "+" : "−"}</span>
                  </button>
                )}
                {(collapsed || !gCollapsed) && (
                  <div className="space-y-0.5">
                    {group.items.map(item => {
                      const active = isActive(item.to, item.exact);
                      return (
                        <Link
                          key={item.to}
                          to={item.to}
                          data-testid={`dp-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                          title={collapsed ? item.label : ""}
                          className={`
                            flex items-center gap-2.5 rounded-lg
                            ${collapsed ? "px-2 py-2 justify-center" : "px-2.5 py-2"}
                            group relative
                            ${active
                              ? "bg-gradient-to-r from-[#FF6A2B]/20 to-[#B12FBB]/10 text-white"
                              : "text-white/70 hover:bg-white/5 hover:text-white"}
                          `}
                        >
                          {active && (
                            <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-gradient-to-b from-[#FF6A2B] to-[#B12FBB]" />
                          )}
                          <span className="text-base flex-shrink-0 leading-none">{item.icon}</span>
                          {!collapsed && (
                            <>
                              <span className="text-[13px] font-semibold flex-1 truncate">{item.label}</span>
                              {item.badge != null && (
                                <span className={`
                                  text-[9px] font-black px-1.5 py-0.5 rounded-full flex-shrink-0
                                  ${active
                                    ? "bg-white/20 text-white"
                                    : "bg-white/5 text-white/60 group-hover:bg-white/10"}
                                `}>
                                  {item.badge}
                                </span>
                              )}
                            </>
                          )}
                          {collapsed && item.badge != null && (
                            <span className="absolute top-1 right-1 h-1.5 w-1.5 rounded-full bg-[#FF6A2B]" />
                          )}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* User pill */}
        <div className="flex-shrink-0 p-2 border-t border-white/5">
          <div className={`flex items-center gap-2 rounded-lg hover:bg-white/5 p-2 ${collapsed ? "justify-center" : ""}`}>
            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[#00C4CC] to-[#5B3AE0] flex items-center justify-center text-white font-black text-xs flex-shrink-0">
              AR
            </div>
            {!collapsed && (
              <div className="min-w-0 flex-1">
                <div className="text-white text-xs font-bold truncate">Andrew R.</div>
                <div className="text-white/50 text-[10px] font-medium truncate">Admin · ProCall</div>
              </div>
            )}
          </div>
          {!collapsed && (
            <Link
              to="/admin"
              className="mt-1 block text-center text-[10px] font-black uppercase tracking-widest text-white/40 hover:text-[#00C4CC] py-1.5 rounded-lg hover:bg-white/5"
              data-testid="dp-exit-preview"
            >
              ← Exit Preview
            </Link>
          )}
        </div>
      </aside>

      {/* ── MAIN ────────────────────────────────────────────────── */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top strip */}
        <header
          data-testid="dp-topbar"
          className="sticky top-0 z-30 h-14 bg-[#0F1F3A]/80 backdrop-blur-lg border-b border-white/5 flex items-center gap-3 px-4"
        >
          <button
            onClick={() => setMobileOpen(true)}
            className="lg:hidden h-9 w-9 rounded-full hover:bg-white/10 text-white flex items-center justify-center"
            data-testid="dp-mobile-menu"
          >
            ☰
          </button>

          {/* Search */}
          <div className="flex-1 max-w-xl relative">
            <input
              type="text"
              placeholder="Search WOs, properties, tenants, vendors…"
              className="w-full h-9 pl-9 pr-3 rounded-full bg-white/5 border border-white/10 text-white text-sm placeholder:text-white/40 focus:outline-none focus:border-[#00C4CC]/50 focus:bg-white/10"
              data-testid="dp-topbar-search"
            />
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">🔍</span>
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 text-[10px] font-black uppercase tracking-widest hidden sm:inline">⌘K</span>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1.5">
            <button className="h-9 px-3 rounded-full bg-[#00C4CC] hover:bg-[#00a8af] text-[#0F1F3A] text-[10px] font-black uppercase tracking-widest whitespace-nowrap hidden sm:inline-flex items-center" data-testid="dp-topbar-new-wo">
              + New WO
            </button>

            {/* Theme picker */}
            <div className="relative">
              <button
                onClick={() => setThemeOpen(o => !o)}
                onBlur={() => setTimeout(() => setThemeOpen(false), 150)}
                className="h-9 w-9 rounded-full hover:bg-white/10 text-white flex items-center justify-center relative"
                data-testid="dp-theme-btn"
                title="Banner theme"
              >
                <div className="h-4 w-4 rounded-full border border-white/40 overflow-hidden flex">
                  {theme.swatch.map((c, i) => (
                    <div key={i} className="flex-1" style={{ background: c }}/>
                  ))}
                </div>
              </button>
              {themeOpen && (
                <div
                  className="absolute right-0 mt-2 w-64 bg-[#0F1F3A] border border-white/10 rounded-xl shadow-2xl p-2 z-50"
                  onMouseDown={(e) => e.preventDefault()}
                  data-testid="dp-theme-menu"
                >
                  <div className="text-[9px] font-black uppercase tracking-widest text-white/40 px-2 py-1.5">
                    Banner theme
                  </div>
                  <div className="space-y-0.5">
                    {Object.values(DP_THEMES).map(t => (
                      <button
                        key={t.id}
                        onClick={() => { setTheme(t.id); setThemeOpen(false); }}
                        data-testid={`dp-theme-${t.id}`}
                        className={`w-full flex items-center gap-2.5 px-2 py-2 rounded-lg text-left ${
                          theme.id === t.id ? "bg-white/10" : "hover:bg-white/5"
                        }`}
                      >
                        <div className="h-6 w-10 rounded-md overflow-hidden flex flex-shrink-0 shadow-inner">
                          {t.swatch.map((c, i) => (
                            <div key={i} className="flex-1" style={{ background: c }}/>
                          ))}
                        </div>
                        <span className="text-[13px] font-semibold text-white flex-1">{t.label}</span>
                        {theme.id === t.id && (
                          <span className="text-[#00C4CC] text-xs font-black">✓</span>
                        )}
                      </button>
                    ))}
                  </div>
                  <div className="text-[9px] font-medium text-white/30 px-2 pt-2 pb-1 border-t border-white/5 mt-1">
                    Preview only · your pick sticks across pages
                  </div>
                </div>
              )}
            </div>

            <button className="h-9 w-9 rounded-full hover:bg-white/10 text-white relative" data-testid="dp-topbar-notifications">
              🔔
              <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-[#FF6A2B]"/>
            </button>
            <button className="h-9 w-9 rounded-full hover:bg-white/10 text-white hidden sm:flex items-center justify-center" data-testid="dp-topbar-help">
              ?
            </button>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 min-w-0">{children}</main>
      </div>
    </div>
  );
}
